/**
 * CZNA-940 — which string a picked register term contributes to the MARC subfield,
 * and how that term is labelled in the dropdown.
 *
 * For most registers the two are the same: the subfield stores the term's preferred
 * heading and the dropdown shows it. A *code-bearing* register is the exception — the
 * MARC field stores a code from an external code list and the heading is only a human
 * name for it. The ISO 639-2/B language register behind 377$a / 41#$9 is the one in
 * production: all 559 087 ACTIVE corpus occurrences of 377$a are 3-letter codes and not
 * one is a name.
 *
 * The picker stored the heading unconditionally until now, which was invisible only
 * because the seeded language register carries the code AS its heading (CZNA-794 had no
 * slot for name-and-code). Where the register did hold names — the test environment,
 * seeded from changesets 029/030 — a cataloguer picking "Čeština (cze)" wrote `Čeština`
 * into 377$a and the record could then be neither saved nor submitted for review
 * (NK, porada 1. 9. 2026). CZNA-942 is about to put Czech names on every language term,
 * which would take that failure everywhere.
 *
 * The same expression — code if the term has one, else its heading — is what
 * `ClosedVocabularyMembership` uses to build the allowed-value set the backend guard
 * checks against, so what a cataloguer can produce and what the backend accepts cannot
 * drift apart.
 */
import { VOCABULARY_TYPE_CONFIG } from '../../../types/api';
/**
 * Mirror of the backend's `VocabularyTypeConfig.CODE_REQUIRED_CODES` — the two types
 * that are code-bearing whatever the registry says.
 *
 * It is a separate constant rather than two rows in {@link VOCABULARY_TYPE_CONFIG}
 * because that map also carries a `labelKey`, and VOCABULARY_GEOGRAPHIC_CODE has no
 * translation: adding it there would either invent a message id or make the map's own
 * contract optional. Keeping the sets apart also makes the mirroring visible — this is
 * the list that has to be changed in step with the Java one.
 */
var CODE_REQUIRED_CODES = new Set([
    'VOCABULARY_LANGUAGE',
    'VOCABULARY_GEOGRAPHIC_CODE',
]);
/**
 * True when this record type's MARC subfield stores a term's CODE rather than its
 * heading.
 *
 * <p>Resolved as the backend resolves it — the UNION of a static list and the registry's
 * `code_required` column ({@code VocabularyTypeConfig.isCodeRequired}), in that order.
 * Both halves matter and for opposite reasons: the static list holds even before the
 * registry query answers (or on a tenant whose registry row is missing), and the registry
 * catches a type an NK admin marks code-required at runtime. Reading only the static list
 * would put the frontend back in the position this whole ticket is about — a second list
 * claiming to be the same register as the backend's, silently disagreeing with it.
 *
 * <p>A record type that is not a vocabulary at all — an authority picker target such as
 * GEOGRAPHIC — matches neither and is therefore false, which is correct: those subfields
 * store the heading.
 *
 * @param registryTypes rows from `GET /czna-authorities/vocabulary-types`; omit when the
 *     caller has none, and only the static list applies.
 */
export var storesVocabularyCode = function (recordType, registryTypes) {
    var _a;
    if (!recordType)
        return false;
    if (CODE_REQUIRED_CODES.has(recordType))
        return true;
    if (((_a = VOCABULARY_TYPE_CONFIG[recordType]) === null || _a === void 0 ? void 0 : _a.codeRequired) === true)
        return true;
    return !!(registryTypes === null || registryTypes === void 0 ? void 0 : registryTypes.some(function (t) { return t.code === recordType && t.codeRequired; }));
};
/**
 * The value to write into the subfield for a picked term.
 *
 * A blank code counts as absent and falls back to the heading — that is the shape the
 * whole seeded language register is in today (CZNA-799: 486 terms, no 901$c), where the
 * heading IS the code, so this returns the same string either way and today's behaviour
 * is unchanged.
 */
export var vocabularyStoredValue = function (recordType, heading, code, registryTypes) {
    var trimmed = code === null || code === void 0 ? void 0 : code.trim();
    return storesVocabularyCode(recordType, registryTypes) && trimmed ? trimmed : heading;
};
/**
 * The dropdown label for a term: `heading (code)` when it has a code, otherwise the
 * heading alone. Applied to every register — a code is worth showing wherever one
 * exists — which is why it takes no record type.
 */
export var vocabularyOptionLabel = function (base, code) {
    var trimmed = code === null || code === void 0 ? void 0 : code.trim();
    return trimmed ? "".concat(base, " (").concat(trimmed, ")") : base;
};
/**
 * CZNA-1033 — a term's vocabulary code read from its record: the first `901 $c`, as the
 * backend's record summary reads it (`RecordServiceImpl`, `MARC_VOCABULARY_CODE_FIELD`).
 *
 * The picker browses the register, whose rows carry no code, so for a code-bearing
 * register the code is read from the picked record at the moment of the pick — one
 * request per pick instead of one per keystroke.
 */
export var vocabularyCodeOf = function (dataFields) {
    var code = (dataFields !== null && dataFields !== void 0 ? dataFields : [])
        .filter(function (field) { return field.fieldTag === '901'; })
        .map(function (field) { var _a, _b; return (_b = (_a = field.subfields.find(function (sf) { return sf.subfieldCode === 'c'; })) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim(); })
        .find(function (value) { return !!value; });
    return code || null;
};
